import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        {/* max-md (<768px): the absolutely-positioned desktop slots become flex items that
            wrap onto two rows (logo + right controls on row 1, scrollable toolbar on row 2).
            All mobile: classes are inert at >= 768px, leaving the desktop layout untouched. */}
        <div className="relative h-[48px] items-center mobile:flex mobile:h-auto mobile:flex-wrap dnarrow:flex dnarrow:h-auto dnarrow:flex-wrap">
          <div className="absolute left-0 top-1/2 flex -translate-y-1/2 items-center mobile:static mobile:translate-y-0 dnarrow:static dnarrow:translate-y-0">
            <div
              className={classNames(
                'mr-3 inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && <Icons.ArrowLeft className="text-white ml-1 h-7 w-7" />}
              <div className="ml-1">
                {WhiteLabeling?.createLogoComponentFn?.(React, props) || <Icons.OHIFLogo />}
              </div>
            </div>
          </div>
          <div className="absolute top-1/2 left-[250px] h-8 -translate-y-1/2 mobile:static mobile:h-auto mobile:translate-y-0 dnarrow:static dnarrow:h-auto dnarrow:translate-y-0">
            {Secondary}
          </div>
          {/* ohif-mobile-toolbar only has styles inside a (max-width: 767px) media query
              (see ui-next assets/styles.css): horizontal scroll strip + 44px tap targets. */}
          <div className="ohif-mobile-toolbar absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform mobile:static mobile:order-last mobile:w-full mobile:translate-x-0 mobile:translate-y-0 mobile:overflow-x-auto dnarrow:static dnarrow:order-last dnarrow:w-full dnarrow:translate-x-0 dnarrow:translate-y-0 dnarrow:overflow-x-auto">
            <div className="flex items-center justify-center space-x-2 mobile:w-max mobile:min-w-full mobile:justify-start dnarrow:w-max dnarrow:min-w-full">
              {children}
            </div>
          </div>
          <div className="absolute right-0 top-1/2 flex -translate-y-1/2 select-none items-center mobile:static mobile:ml-auto mobile:translate-y-0 dnarrow:static dnarrow:ml-auto dnarrow:translate-y-0">
            {UndoRedo}
            <div className="border-black mx-1.5 h-[25px] border-r mobile:hidden"></div>
            {PatientInfo}
            <div className="border-black mx-1.5 h-[25px] border-r mobile:hidden"></div>
            <div className="flex-shrink-0">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-[#1a1a1a] mt-2 h-full w-full"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {IconComponent && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={IconComponent.name} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
